import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Currency,
  DateTime,
  DeleteConfirmation,
  Icon,
  IconButton,
  ListView,
  ListViewActions,
  ListViewMenu,
  ListViewSelection,
  Page,
  Tooltip,
} from '~/components';
import { BulkActionMenu } from '~/components/bulk-action';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useAuth, useRowSelection } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors } from '~/styles';
import TitleTooltip from '../../TitleTooltip';
import AddMultipleDialog from './AddMultipleDialog';
import BulkDelete from './BulkDelete';
import InvoiceMilestoneForm from './InvoiceMilestoneForm';
import SettingsActionsBanner from '../../SettingsActionsBanner';

export default function BillingSchedule({ project }) {
  const auth = useAuth();
  const canEdit = project.permissions.edit && auth.projects.sections.billing;
  const api = useApi();
  const { workspace } = useWorkspace();
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).projectSettings(project.id).invoiceMilestones().get();
    setData(data);
  }, [api, workspace.id, project]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const selection = useRowSelection({ rows: data });

  const [dialog, setDialog] = useState(null);
  const toast = useToast();

  const handleSaved = async () => {
    await fetchData();
    setDialog(null);
  };

  const handleShowForm = (invoiceMilestone = null) => {
    setDialog(
      <InvoiceMilestoneForm
        currency={project.currency}
        projectId={project.id}
        invoiceMilestoneId={invoiceMilestone?.id}
        readOnly={!canEdit}
        onSaved={handleSaved}
        onClose={() => setDialog(null)}
      />,
    );
  };

  const handleShowMultipleForm = () => {
    setDialog(
      <AddMultipleDialog
        currency={project.currency}
        project={project}
        onSaved={handleSaved}
        onCancel={() => setDialog(null)}
      />,
    );
  };

  const confirmation = useConfirmation();
  const handleDelete = async (invoiceMilestone) => {
    await confirmation.prompt((resolve) => (
      <DeleteConfirmation
        title="Delete Billing Schedule Item"
        resolve={async (result) => {
          if (result) {
            await api.www
              .workspaces(workspace.id)
              .projectSettings(project.id)
              .invoiceMilestones(invoiceMilestone.id)
              .delete();
            toast.success('Billing Schedule Item deleted successfully.');
            await fetchData();
            selection.deselect(invoiceMilestone.id);
          }
          resolve(result);
        }}>
        Are you sure you want to delete this billing schedule item?
      </DeleteConfirmation>
    ));
  };

  const handleBulkDelete = async () => {
    await confirmation.prompt((resolve) => (
      <BulkDelete
        projectId={project.id}
        ids={selection.ids}
        currency={project.currency}
        onComplete={async () => {
          selection.clear();
          await fetchData();
        }}
        onClose={resolve}
      />
    ));
  };

  if (!data) return <PageLoader />;

  return (
    <>
      <Page.TabHeader>
        <Page.Title>Fixed Fee Billing Schedule</Page.Title>
        <TitleTooltip message="Schedule the billing of the fixed fee project revenue." />

        {canEdit && (
          <Page.Actions>
            <IconButton icon="clone-plus" tooltip="Add Multiple" onClick={handleShowMultipleForm} />

            <IconButton icon="plus" tooltip="Add" onClick={() => handleShowForm(null)} />
          </Page.Actions>
        )}
      </Page.TabHeader>

      <Page.ListView>
        <ListView>
          <ListView.Header>
            <ListViewSelection.Column
              testId="invoice_milestones_bulk_checkbox"
              selection={selection}
              isVisible={canEdit}
              disabled={!data.length}
            />
            <ListView.Column width="8rem">Date</ListView.Column>
            <ListView.Column minWidth="10rem">Invoice Item</ListView.Column>
            <ListView.Column minWidth="20rem">Notes</ListView.Column>
            <ListView.Column width="10rem" align="right">
              Amount
            </ListView.Column>
            <ListView.Column width="3rem" />
            <ListViewActions.Column />
          </ListView.Header>

          <ListView.Body>
            {data.map((milestone) => {
              const isInvoiced = Boolean(milestone.invoice);
              const isDraftInvoiced = milestone.invoice?.statusId === 'draft';
              const areActionsDisabled = isInvoiced;
              const tooltip = isInvoiced ? "Invoiced billing schedule items can't be modified." : undefined;

              return (
                <ListView.Row
                  key={milestone.id}
                  onClick={canEdit && areActionsDisabled ? undefined : () => handleShowForm(milestone)}>
                  <ListViewSelection.Cell id={milestone.id} selection={selection} testId="checkbox_invoice_milestone" />
                  <ListView.Cell>
                    <DateTime value={milestone.date} />
                  </ListView.Cell>
                  <ListView.Cell>{milestone.invoiceItem.name}</ListView.Cell>
                  <ListView.Cell>{milestone.details}</ListView.Cell>
                  <ListView.Cell>
                    <Currency value={milestone.fee} currency={milestone.project.currency} />
                  </ListView.Cell>
                  <ListView.Cell>
                    {isInvoiced && (
                      <Tooltip
                        message={
                          isDraftInvoiced
                            ? `This billing schedule item is on draft invoice #${milestone.invoice.number}.`
                            : `This billing schedule item is on published invoice #${milestone.invoice.number}.`
                        }>
                        <Icon icon="file-invoice-dollar" color={isDraftInvoiced ? colors.warning : colors.primary} />
                      </Tooltip>
                    )}
                  </ListView.Cell>
                  <ListViewActions>
                    {canEdit ? (
                      <>
                        <ListViewActions.Edit
                          disabled={areActionsDisabled}
                          tooltip={tooltip}
                          onClick={() => handleShowForm(milestone)}
                        />
                        <hr />
                        <ListViewMenu>
                          <ListViewMenu.Item disabled={areActionsDisabled} onClick={() => handleShowForm(milestone)}>
                            Edit
                          </ListViewMenu.Item>

                          <ListViewMenu.DeleteItem
                            disabled={areActionsDisabled}
                            tooltip={tooltip}
                            onClick={() => handleDelete(milestone)}>
                            Delete
                          </ListViewMenu.DeleteItem>
                        </ListViewMenu>
                      </>
                    ) : (
                      <ListViewActions.View onClick={() => handleShowForm(milestone)} />
                    )}
                  </ListViewActions>
                </ListView.Row>
              );
            })}

            {data.length > 0 && (
              <ListView.TotalRow>
                <ListView.Cell />
                <ListView.Cell>Total</ListView.Cell>
                <ListView.Cell />
                <ListView.Cell />
                <ListView.Cell>
                  <Currency value={_.sumBy(data, 'fee')} currency={project.currency} />
                </ListView.Cell>
                <ListView.Cell />
                <ListViewActions.Cell />
              </ListView.TotalRow>
            )}

            {data.length === 0 && <ListView.Empty />}
          </ListView.Body>

          <ListView.Status total={data.length} label="Billing Schedule Item" />
        </ListView>
      </Page.ListView>

      {dialog}

      {selection.any && (
        <SettingsActionsBanner>
          <BulkActionMenu count={selection.count}>
            {({ setIsOpen }) => (
              <BulkActionMenu.Item
                onClick={() => {
                  setIsOpen(false);
                  handleBulkDelete();
                }}>
                Delete
              </BulkActionMenu.Item>
            )}
          </BulkActionMenu>
        </SettingsActionsBanner>
      )}
    </>
  );
}
